// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { useMergeRefs } from '@cloudscape-design/component-toolkit/internal';
import { getBaseProps } from '../../base-component';
import { GroupedControlContext, } from '../../context/control-group-context';
import { useFitsInline } from '../../hooks/use-fits-inline';
import { flattenChildren } from '../../utils/flatten-children';
import styles from './styles.css.js';
import testUtilStyles from './test-classes/styles.css.js';
const InternalControlGroup = forwardRef(({ children, direction = 'auto', ...props }, ref) => {
    const baseProps = getBaseProps(props);
    const flattenedChildren = flattenChildren(children, 'ControlGroup');
    const controlCount = flattenedChildren.length;
    // Only `'auto'` measures; a forced direction wins and the ghost is not rendered.
    const { overflows, rootRef, ghostRef } = useFitsInline();
    const mergedRootRef = useMergeRefs(ref, rootRef);
    const resolvedDirection = direction === 'auto' ? (overflows ? 'vertical' : 'horizontal') : direction;
    const renderControlSlots = () => flattenedChildren.map((child, index) => {
        const key = child && typeof child === 'object' ? child.key : undefined;
        const position = controlCount === 1 ? 'only' : index === 0 ? 'first' : index === controlCount - 1 ? 'last' : 'middle';
        return (React.createElement("div", { key: key ? String(key) : index, className: clsx(testUtilStyles.control, styles.control, styles[`control-${position}-${resolvedDirection}`]) },
            React.createElement(GroupedControlContext.Provider, { value: { position, direction: resolvedDirection } }, child)));
    });
    return (React.createElement("div", { ...baseProps, ref: mergedRootRef, role: "group", className: clsx(baseProps.className, testUtilStyles.root, styles.root, styles[`root-${resolvedDirection}`]) },
        renderControlSlots(),
        direction === 'auto' && (React.createElement("div", { ref: ghostRef, className: styles.ghost, "aria-hidden": "true" }, renderControlSlots()))));
});
export default InternalControlGroup;
//# sourceMappingURL=index.js.map